import { ReactNode, useId } from 'react';

import { Code } from '@components/patterns/Code';
import useCopyToClipboard from '@components/patterns/CopyButton';
import { DataList } from '@components/patterns/DataList';
import { PrettyJSONField } from '@components/patterns/PrettyJSON/field';
import { PrettyJSONGroup } from '@components/patterns/PrettyJSON/group';
import { AnimatedIcon } from '@components/primitives/AnimatedIcon';
import { Button } from '@components/primitives/Button';
import { Tabs } from '@components/primitives/Tabs';
import { toast } from '@vault/Toast';

import css from './styles.module.css';

export interface PrettyJSONProps {
  children: ReactNode;
  label?: string;
  json: any;
}

function PrettyJSONRoot({ children, label, json }: PrettyJSONProps) {
  const { copy, copied } = useCopyToClipboard();

  const id = useId();
  async function copyJSON() {
    await copy(JSON.stringify(json, null, 2));
    toast('Copied JSON', { id: `copy-${id}-json` });
  }

  return (
    <div className={css.container}>
      <Tabs
        className={css.tabs}
        size="sm"
        variant="secondary"
        suffix={
          <Button size="xs" variant="secondary" ghost onClick={copyJSON}>
            {
              <AnimatedIcon.CopyCheck
                className={css.copyIcon}
                state={copied ? 'check' : 'copy'}
              />
            }
            Copy
          </Button>
        }
      >
        <Tabs.Tab label={label ?? 'Formatted'}>
          <div className={css.formatted}>
            <DataList size="sm">{children}</DataList>
          </div>
        </Tabs.Tab>

        <Tabs.Tab label="JSON">
          <Code className={css.json} language="json">
            {JSON.stringify(json, null, 2)}
          </Code>
        </Tabs.Tab>
      </Tabs>
    </div>
  );
}

export const PrettyJSON = Object.assign(PrettyJSONRoot, {
  Group: PrettyJSONGroup,
  Field: PrettyJSONField,
});
